<script lang="ts">
    import { DRAWER_POSITION } from "@/constants/drawer";
    import { REWARDS_WIDGET_NAMES } from "@/constants/widget-names";
    import { TRANSLATION_NAMESPACES } from "@apps/rewards/constants/constants";
    import { RewardsHubWidgetDefinition } from "@apps/rewards/constants/definitions";
    import { BRANDING_LAUNCHER_POSITION } from "@apps/rewards/modules/StorefrontValues/constants";
    import type RewardsStorefrontValuesService from "@apps/rewards/modules/StorefrontValues/service/RewardsStorefrontValuesService";
    import RewardsDiResolverService from "@apps/rewards/services/RewardsDiResolverService/RewardsDIResolverService";
    import type { TranslationNamespaces } from "@apps/rewards/types";
    import type CustomizationManager from "@lib/CustomizationManager/CustomizationManager";
    import type { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
    import type { GwI18n } from "@modules/i18n/GwI18n/GwI18n";
    import WidgetInjectedSdkEvent from "@modules/sdk/common_sdk/events/WidgetInjectedSdkEvent";
    import { dispatchSdkEvent } from "@modules/sdk/helpers";
    import type { FocusTrapReturnType } from "@ui-utils/actions/focusTrap";
    import { focusTrap } from "@ui-utils/actions/focusTrap";
    import { onMount } from "svelte";

    import Drawer from "@components/Drawer/Drawer.svelte";
    import type DrawerManager from "@components/Drawer/DrawerManager";
    import DrawerSidebar from "@components/Drawer/DrawerSidebar.svelte";
    import DynamicImport from "@components/DynamicImport/DynamicImport.svelte";
    import Portal from "@components/Portal/Portal.svelte";
    import Widget from "@components/Widget.svelte";
    import type { LoggerInterface } from "@interfaces/LoggerInterface";

    import RewardsDrawerButton from "./components/RewardsDrawerButton.svelte";

    //Props
    export let storefrontValuesService: RewardsStorefrontValuesService;
    export let i18n: GwI18n;
    export let dynamicImportManager: DynamicImportManager;
    export let logger: LoggerInterface;
    export let drawerManager: DrawerManager;
    export let customizationManager: CustomizationManager;
    export let widgetName: REWARDS_WIDGET_NAMES;
    export let widgetNamespace: TranslationNamespaces;
    export let afterWidgetInject: () => void;

    const branding$ = storefrontValuesService.selectRewardsBranding();
    const isDrawerVisible$ = drawerManager.visible$;
    const drawerLabel$ = i18n.selectT(widgetNamespace, "launcher_label");
    let focusTrapTargetRef: HTMLElement | null = null;
    let focusTrapInstance: FocusTrapReturnType;

    // TODO: Temporary solution - drawer position is tied to launcher position setting.
    //       Refactor when drawer and launcher positions can be configured independently.
    const widgetPosition = {
        [BRANDING_LAUNCHER_POSITION.bottomRight]: DRAWER_POSITION.RC,
        [BRANDING_LAUNCHER_POSITION.bottomLeft]: DRAWER_POSITION.LC,
    };

    const onElementMount = (target: HTMLElement) => {
        if (focusTrapTargetRef) {
            focusTrapInstance = focusTrap(focusTrapTargetRef, {
                enabled: true,
                elements: [focusTrapTargetRef, target],
                options: {
                    initialFocus: false,
                    allowOutsideClick: true,
                },
            });
        }
    };

    onMount(() => afterWidgetInject());

    $: if (!$isDrawerVisible$) focusTrapInstance?.destroy();
</script>

<DynamicImport
    let:value={{
        module,
        importModule,
    }}
    definition={RewardsHubWidgetDefinition}
    shouldImportModule={$isDrawerVisible$}
    {dynamicImportManager}
>
    {#if $branding$}
        {@const direction =
            widgetPosition[$branding$.popUpWidget.launcher.position.placement]}
        <Drawer drawerClass="gw-rd-drawer" {direction} {drawerManager}>
            <Portal
                order={3}
                Component={RewardsDrawerButton}
                componentProps={{
                    branding$,
                    drawerLabel$,
                    direction,
                }}
                componentListeners={{
                    click: () => drawerManager.drawerShow(),
                    mouseenter: () => importModule(),
                }}
                selector=".gw-widget-placeholder-{direction}"
                {customizationManager}
                {widgetName}
                shadow
            />
            <div
                slot="sidebar"
                class="gw-rd-drawer__sidebar-wrapper"
                bind:this={focusTrapTargetRef}
            >
                <DrawerSidebar {drawerManager}>
                    {#if $isDrawerVisible$ && module}
                        <Widget
                            class="gw-rd-popup-root-widget-container"
                            widget={module}
                            params={[
                                {
                                    isWidgetShadowRootEnabled: false,
                                    onElementMount,
                                    widgetName,
                                    translations: {
                                        widgetNamespace:
                                            TRANSLATION_NAMESPACES.popUpWidget,
                                        criticalWidgetNamespace:
                                            TRANSLATION_NAMESPACES.criticalPopUpWidget,
                                        pointsNamespace:
                                            TRANSLATION_NAMESPACES.commonCriticalPoints,
                                    },
                                    diResolverService:
                                        new RewardsDiResolverService(),
                                },
                                logger,
                            ]}
                            on:widgetInit={() =>
                                dispatchSdkEvent(
                                    new WidgetInjectedSdkEvent(
                                        REWARDS_WIDGET_NAMES.REWARDS_HUB_WIDGET
                                    )
                                )}
                        />
                    {/if}
                </DrawerSidebar>
            </div>
        </Drawer>
    {/if}
</DynamicImport>

<style lang="postcss">
    .gw-rd-drawer__sidebar-wrapper {
        height: 100%;
    }

    :global(.gw-rd-drawer .gw-drawer-sidebar .gw-drawer-sidebar__close_btn) {
        top: 10px;
        z-index: 3;
        color: var(--gw-white-text-color);
    }
</style>
